import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useInviteStudent, useInviteTeacher } from '@/features/membership/hooks';
import { ROLE_LABELS } from '@/features/membership/labels';
import type { InviteToOrganizationResult } from '@/features/membership/types';
import { ApiError } from '@/lib/api/client';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Invites a Teacher or a Student (ADR-027, Decisions 4-5) - one dialog for
 * both, since the two forms differ only by the Student-only optional
 * guardian contact fields. Deliberately collects nothing beyond what
 * ADR-027's own request shape defines: no role-specific wizard, no
 * invented fields (Jobs 32-33).
 */
export function InviteMemberDialog({
  role,
  open,
  onOpenChange,
  onInvited,
}: {
  readonly role: 'Teacher' | 'Student';
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onInvited: (result: InviteToOrganizationResult) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const inviteTeacher = useInviteTeacher();
  const inviteStudent = useInviteStudent();
  const invite = role === 'Teacher' ? inviteTeacher : inviteStudent;

  function reset(): void {
    setEmail('');
    setGuardianName('');
    setGuardianPhone('');
    setValidationError(null);
    inviteTeacher.reset();
    inviteStudent.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (!EMAIL_PATTERN.test(email.trim())) {
      setValidationError('Masukkan alamat email yang valid.');
      return;
    }

    const onSuccess = {
      onSuccess: (result: InviteToOrganizationResult) => {
        onInvited(result);
        handleOpenChange(false);
      },
    };

    if (role === 'Teacher') {
      inviteTeacher.mutate({ email: email.trim() }, onSuccess);
    } else {
      inviteStudent.mutate(
        {
          email: email.trim(),
          guardianName: guardianName.trim().length > 0 ? guardianName.trim() : null,
          guardianPhone: guardianPhone.trim().length > 0 ? guardianPhone.trim() : null,
        },
        onSuccess,
      );
    }
  }

  const apiErrorMessage =
    invite.error instanceof ApiError
      ? invite.error.message
      : invite.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Undang {ROLE_LABELS[role]}</DialogTitle>
          <DialogDescription>
            Undangan akan menghasilkan tautan penerimaan yang dapat Anda salin dan kirim secara
            manual.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="member-email">Email</Label>
            <Input
              id="member-email"
              type="text"
              inputMode="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              placeholder={role === 'Teacher' ? 'guru@contoh.com' : 'santri@contoh.com'}
              autoComplete="email"
            />
          </div>

          {role === 'Student' && (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="member-guardian-name">Nama Wali (opsional)</Label>
                <Input
                  id="member-guardian-name"
                  type="text"
                  value={guardianName}
                  onChange={(event) => {
                    setGuardianName(event.target.value);
                  }}
                  placeholder="Nama wali santri"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="member-guardian-phone">Telepon Wali (opsional)</Label>
                <Input
                  id="member-guardian-phone"
                  type="text"
                  inputMode="tel"
                  value={guardianPhone}
                  onChange={(event) => {
                    setGuardianPhone(event.target.value);
                  }}
                  placeholder="08xxxxxxxxxx"
                />
              </div>
            </>
          )}

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Mengirim...' : 'Kirim Undangan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
